<template>
    <v-data-table
        class="reports-list"
        :hideDefaultFooter="true"
        :itemsPerPage="-1"
        :headers="headers"
        :items="reports"
        @click:row="onClickRow"
    >
        <template
            v-for="header in headers"
            #[`header.${header.value}`]
            :key="header.value"
        >
            <p
                v-title-clamp
                class="text-body-m text-medium text-secondary-500 text-ellipsis"
            >
                {{ header.title }}
            </p>
        </template>
        <template
            #[`item.title`]="{ item }"
        >
            <p
                v-title-clamp
                class="text-body-m text-medium text-clamp-2"
            >
                {{ item.title }}
            </p>
        </template>
        <template
            #[`item.screenshot`]="{ item }"
        >
            <div
                class="deepi-row align-center"
            >
                <img
                    :height="44"
                    :src="item.screenshotUrl"
                    class="w-full h-full object-cover"
                />
            </div>
        </template>
        <template
            #[`item.status`]="{ item }"
        >
            <ReportStatusChip
                :status="item.status"
            />
        </template>
        <template
            #[`item.client`]="{ item }"
        >
            <div
                class="deepi-col gap-4"
            >
                <p
                    v-title-clamp
                    class="text-body-m text-medium text-ellipsis"
                >
                    {{ item.client }}
                </p>
                <p
                    v-title-clamp
                    class="text-body-s text-secondary-500 text-medium text-ellipsis"
                >
                    {{ item.organization }}
                </p>
            </div>
        </template>
        <template
            #[`item.user`]="{ item }"
        >
            <div
                class="deepi-col gap-4"
            >
                <p
                    v-title-clamp
                    class="text-body-m text-medium text-ellipsis"
                >
                    {{ item.lastname }}
                </p>
                <p
                    v-title-clamp
                    class="text-body-s text-secondary-500 text-medium text-ellipsis"
                >
                    {{ item.firstname }}
                </p>
            </div>
        </template>
        <template
            #[`item.createdAt`]="{ item }"
        >
            <p
                v-title-clamp
                class="text-body-m text-medium text-ellipsis"
            >
                {{ item.createdAt.formatLocale('L HH:mm') }}
            </p>
        </template>
    </v-data-table>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import { useFetchClient } from '@deepidoo/playdoo/services/composables';

import { useTranslations } from '../../composables';
import { Report, type ReportDTO } from '../../models';

import ReportStatusChip from '../reportStatusChip/ReportStatusChip.vue';

const emit = defineEmits<{
    'click:row': [report: Report];
}>();

const { post } = useFetchClient();
const { tr } = useTranslations();

const reports = ref<Report[]>([]);

const headers = computed((): Record<string, any>[] => [{
    value: 'screenshot',
    title: '',
    width: '10%'
}, {
    value: 'title',
    title: tr('report.properties.title'),
    width: '25%'
}, {
    value: 'status',
    title: tr('report.properties.status'),
    width: '15%'
}, {
    value: 'client',
    title: tr('report.properties.client'),
    width: '20%'
}, {
    value: 'user',
    title: tr('report.properties.user'),
    width: '15%%'
}, {
    value: 'createdAt',
    title: tr('report.properties.created-at'),
    width: '15%'
}]);

const onClickRow = (_event: MouseEvent, row: { item: Report }): void => {
    emit('click:row', row.item);
};

const getManyReports = async (): Promise<void> => {
	const response = await post<{ data: { reports: ReportDTO[] }}>(
		`${import.meta.env.VITE_REPORT_URL}graphql`,
		{
			query: `
				query Reports {
					reports {
						id
                        status
                        category
                        title
                        description
                        pageUrl
                        screenshotUrl
                        firstname
                        lastname
                        email
                        phone
                        client
                        organization
                        settings {
                            browser
                            os
                            width
                            height
                        }
                        createdAt
					}
				}
			`
		}
	);

    if (response.data && response.data.reports) {
        reports.value = response.data.reports.map(dto => new Report(dto));
    }
};

onMounted((): void => {
    getManyReports();
});
</script>

<style scoped lang="scss">
@use 'ReportsList.scss';
</style>